import { Check } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAnalytics } from 'use-analytics';
import { EntryPath } from '@/Config/app/router/entry.path';
import { trackAndNavigate } from '@/Config/libs/analytics';
import { ReCaptchaCheckbox } from '@/Domain/components/Auth/ReCaptchaCheckbox';
import { ReCaptchaV3 } from '@/Domain/components/Auth/ReCaptchaV3';
import { Alert, AlertDescription } from '@/Domain/components/Shared/Alert/Alert';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/Domain/components/Shared/Field/Field';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import { EnvelopIcon } from '@/Domain/components/icons/Envelop.icon';
import { useForgotPasswordHook } from '@/Domain/hooks/Auth/useForgotPassword.hook';
import { cn } from '@/Domain/utils/cn';

// TODO: try do remove recaptcha V2
export const ForgotPasswordPage = () => {
  const { t } = useTranslation(['forgot_password', 'forgot_password_success', 'validation']);

  const navigate = useNavigate();
  const { search } = useLocation();
  const { track } = useAnalytics();
  const successHeadingRef = useRef<HTMLHeadingElement>(null);
  const loginPath = `${EntryPath.login}${search}`;

  const {
    register,
    errors,
    control,
    handleSubmit,
    escalateToV2,
    handleV2Focus,
    shouldFocusV2,
    useV2,
    forgot,
    onSubmit,
    onVerify,
    resetRequest,
    isPending,
  } = useForgotPasswordHook();

  useEffect(() => {
    if (forgot.isSuccess) {
      successHeadingRef.current?.focus();
    }
  }, [forgot.isSuccess]);

  const handleBackToLogin = () => {
    trackAndNavigate(
      'forgot_password_back_success_click',
      { event_category: 'Forgot password', event_label: 'forgot_password_form' },
      () => navigate(loginPath, { viewTransition: true }),
    );
  };

  const handleTryAnotherEmail = () => {
    track('forgot_password_try_another_email_click', {
      event_category: 'Forgot password',
      event_label: 'forgot_password_form',
    });
    resetRequest();
  };

  if (forgot.isSuccess) {
    return (
      <div className="animate-in fade-in duration-300" data-testid="forgot-password-success">
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-success-muted">
            <Check aria-hidden="true" className="h-8 w-8 text-success" strokeWidth={2} />
          </div>
          <h1
            ref={successHeadingRef}
            className="mb-2 text-2xl font-semibold text-foreground"
            tabIndex={-1}
          >
            {t('forgot_password_success:title')}
          </h1>
          <p
            className="text-pretty text-muted-foreground"
            data-testid="forgot-password-success-description"
          >
            {t('forgot_password_success:description')}
          </p>
        </div>

        <div className="flex flex-col gap-3">
          <Button
            variant="dark"
            size="lg"
            className="w-full"
            onClick={handleBackToLogin}
            data-testid="forgot-password-back-to-login"
          >
            {t('forgot_password_success:link')}
          </Button>
          <Button
            variant="secondary"
            size="lg"
            className="w-full"
            onClick={handleTryAnotherEmail}
            data-testid="forgot-password-try-another-email"
          >
            {t('forgot_password_success:try_another_email')}
          </Button>
        </div>

        <div className="mt-6 grid gap-4 text-center">
          <Separator />
          <p className="text-sm text-muted-foreground">
            {t('forgot_password_success:no_account_prompt')}{' '}
            <Link
              to={`/${EntryPath.register}${search}`}
              className="font-medium text-foreground transition-colors hover-underline"
              data-testid="forgot-password-register-link"
              onClick={() =>
                track('forgot_password_register_click', {
                  event_category: 'Forgot password',
                  event_label: 'forgot_password_form',
                })
              }
            >
              {t('forgot_password_success:register_link')}
            </Link>
          </p>
        </div>

        <p
          className="mt-6 text-center text-sm text-muted-foreground"
          data-testid="forgot-password-google-hint"
        >
          {t('forgot_password:google_hint')}
        </p>
      </div>
    );
  }

  return (
    <div className="animate-in fade-in duration-300">
      {/* Header */}
      <div className="text-center mb-6">
        <h1 className="mb-2 text-2xl font-semibold text-foreground">
          {t('forgot_password:title')}
        </h1>
        <p className="text-sm text-muted-foreground">{t('forgot_password:subtitle')}</p>
        <p className="mt-3 text-sm text-muted-foreground" data-testid="forgot-password-google-hint">
          {t('forgot_password:google_hint')}
        </p>
      </div>

      <div className="grid gap-5">
        {/* Form */}
        <form
          id="forgot-password-form"
          className="flex flex-col gap-5"
          onSubmit={handleSubmit(onSubmit)}
        >
          <ReCaptchaV3 onVerify={onVerify} onUnavailable={escalateToV2} />

          <FieldGroup>
            <Field data-invalid={!!errors.email}>
              <FieldLabel htmlFor="forgot-password-email">
                {t('forgot_password:form.email')}
              </FieldLabel>
              <Input
                id="forgot-password-email"
                size="lg"
                data-testid="forgot-password-email"
                placeholder={t('forgot_password:form.email_placeholder')}
                error={!!errors.email}
                aria-invalid={!!errors.email}
                aria-describedby={errors.email ? 'forgot-password-email-error' : undefined}
                autoFocus
                autoComplete="email"
                startIcon={<EnvelopIcon className="size-4 text-foreground-subtle" />}
                {...register('email')}
              />
              <div
                className={cn(
                  'grid transition-[grid-template-rows] duration-200 ease-out',
                  errors.email ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                )}
              >
                <div className="overflow-hidden">
                  <FieldError id="forgot-password-email-error" className="pt-1">
                    {errors.email?.message}
                  </FieldError>
                </div>
              </div>
            </Field>
          </FieldGroup>

          {errors.root && (
            <Alert
              variant="destructive"
              data-testid="forgot-password-error"
              className="animate-in fade-in duration-200"
            >
              <AlertDescription>{errors.root.message}</AlertDescription>
            </Alert>
          )}

          {/* reCAPTCHA v2 Fallback */}
          {useV2 && (
            <Controller
              name="recaptcha_response_v2"
              control={control}
              render={({ field: { onChange, value }, fieldState: { invalid } }) => (
                <ReCaptchaCheckbox
                  onChange={onChange}
                  onFocusHandled={handleV2Focus}
                  value={value}
                  isInvalid={invalid}
                  shouldFocus={shouldFocusV2}
                />
              )}
            />
          )}
          {errors.recaptcha_response_v2 && (
            <FieldError className="text-center" data-testid="forgot-password-captcha-error">
              {t('validation:required')}
            </FieldError>
          )}

          {/* Submit Button */}
          <Button
            variant="dark"
            size="lg"
            type="submit"
            loading={isPending}
            className="w-full mt-1 max-sm:h-auto max-sm:min-h-12 max-sm:whitespace-normal"
            data-testid="forgot-password-submit"
          >
            {t('forgot_password:button.submit')}
          </Button>
        </form>

        <div className="grid gap-4 text-center">
          <Separator />
          <p className="text-sm text-muted-foreground">
            {t('forgot_password:back_prompt')}{' '}
            <Link
              to={loginPath}
              className="font-medium text-foreground transition-colors hover-underline"
              onClick={() =>
                track('forgot_password_back_click', {
                  event_category: 'Forgot password',
                  event_label: 'forgot_password_form',
                })
              }
            >
              {t('forgot_password:link.login')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
